import { toast } from '../../common_components/Toast/ToastUtils';
import XLSX from 'xlsx-js-style';
// ─── Styles ───────────────────────────────────────────────────────────────────
const titleStyle = {
    font: { bold: true, sz: 18, color: { rgb: '181D27' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: '374151' } },
    fill: { fgColor: { rgb: 'E5E7EB' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const dataCellStyle = {
    font: { sz: 11, color: { rgb: '101828' } },
    alignment: { horizontal: 'left', vertical: 'center' },
};
const numericCellStyle = {
    font: { sz: 11, color: { rgb: '101828' } },
    alignment: { horizontal: 'right', vertical: 'center' },
};
const tagStyleMap = {
    TOF: { rgb: '175cd3' },
    MOF: { rgb: '15803d' },
    BOF: { rgb: '7e22ce' },
    SPAM: { rgb: 'b91c1c' },
    'No Funnel': { rgb: 'c2410c' },
};
// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
    if (!dateStr)
        return '';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    catch {
        return dateStr;
    }
};
const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const formatNumber = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString('en-US');
};
// ─── Main export ──────────────────────────────────────────────────────────────
export function exportPublisherTagging(rows, startDate, endDate) {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
        toast.info('No data available to export.');
        return;
    }
    try {
        const dateRange = startDate && endDate
            ? `${formatDate(startDate)} – ${formatDate(endDate)}`
            : '';
        const titleText = `Publisher Tagging ${dateRange ? `  |  ${dateRange}` : ''}`;
        // ─── Headers ──────────────────────────────────────────────────────────
        const headers = [
            'Publisher Name',
            'Platform',
            'G8A Publisher Name',
            'G8A Publisher ID',
            'Client',
            'Tag',
            'Publisher URL',
            'Date',
            'Revenue',
            'Clicks',
            'Orders',
        ];
        const numCols = headers.length;
        // ─── Summary rows (parent) ─────────────────────────────────────────────
        const dataRows = [];
        rows.forEach((row) => {
            const s = row.summary;
            // Parent / summary row
            dataRows.push([
                s.publisherName || '',
                s.platform || '',
                s.g8aPublisherName || '',
                s.g8aPublisherId || '',
                s.client || '',
                s.tag || '',
                s.publisherUrl || '',
                formatDate(s.date),
                formatCurrency(s.revenue),
                formatNumber(s.clicks),
                formatNumber(s.orders),
            ]);
            // Child / detail rows (indented publisher name)
            if (row.details && row.details.length > 0) {
                row.details.forEach((d) => {
                    dataRows.push([
                        `  └ ${d.publisherName || ''}`,
                        d.platform || '',
                        d.g8aPublisherName || '',
                        d.g8aPublisherId || '',
                        d.client || '',
                        d.tag || '',
                        d.publisherUrl || '',
                        formatDate(d.date),
                        formatCurrency(d.revenue),
                        formatNumber(d.clicks),
                        formatNumber(d.orders),
                    ]);
                });
            }
        });
        // ─── Build sheet ──────────────────────────────────────────────────────
        const titleRow = [titleText, ...Array(numCols - 1).fill('')];
        const spacerRow = Array(numCols).fill('');
        const sheetData = [titleRow, spacerRow, headers, ...dataRows];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        // Title merge + style
        worksheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
        ];
        if (worksheet['A1'])
            worksheet['A1'].s = titleStyle;
        // Header row styles (row index 2)
        for (let c = 0; c < numCols; c++) {
            const cell = XLSX.utils.encode_cell({ r: 2, c });
            if (worksheet[cell])
                worksheet[cell].s = columnHeaderStyle;
        }
        // Data rows styling
        const dataStartRow = 3;
        let sheetRowIndex = dataStartRow;
        rows.forEach((row) => {
            const s = row.summary;
            // Summary row cells
            for (let c = 0; c < numCols; c++) {
                const cell = XLSX.utils.encode_cell({ r: sheetRowIndex, c });
                if (!worksheet[cell])
                    continue;
                const isNumeric = c >= 8; // Revenue, Clicks, Orders
                worksheet[cell].s = isNumeric ? numericCellStyle : dataCellStyle;
                // Tag cell colour (col index 5)
                if (c === 5 && s.tag && tagStyleMap[s.tag]) {
                    worksheet[cell].s = {
                        ...dataCellStyle,
                        font: { ...dataCellStyle.font, color: tagStyleMap[s.tag], bold: true },
                    };
                }
            }
            sheetRowIndex++;
            // Detail rows
            if (row.details && row.details.length > 0) {
                row.details.forEach((d) => {
                    for (let c = 0; c < numCols; c++) {
                        const cell = XLSX.utils.encode_cell({ r: sheetRowIndex, c });
                        if (!worksheet[cell])
                            continue;
                        const isNumeric = c >= 8;
                        worksheet[cell].s = {
                            ...(isNumeric ? numericCellStyle : dataCellStyle),
                            font: { ...(isNumeric ? numericCellStyle : dataCellStyle).font, color: { rgb: '6b7280' } },
                        };
                        // Tag colour for detail row
                        if (c === 5 && d.tag && tagStyleMap[d.tag]) {
                            worksheet[cell].s = {
                                ...dataCellStyle,
                                font: { ...dataCellStyle.font, color: tagStyleMap[d.tag], sz: 10 },
                            };
                        }
                    }
                    sheetRowIndex++;
                });
            }
        });
        // Column widths
        worksheet['!cols'] = [
            { wch: 28 }, // Publisher Name
            { wch: 14 }, // Platform
            { wch: 26 }, // G8A Publisher Name
            { wch: 22 }, // G8A Publisher ID
            { wch: 18 }, // Client
            { wch: 12 }, // Tag
            { wch: 32 }, // Publisher URL
            { wch: 14 }, // Date
            { wch: 14 }, // Revenue
            { wch: 12 }, // Clicks
            { wch: 12 }, // Orders
        ];
        // ─── Write file ───────────────────────────────────────────────────────
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Publisher Tagging');
        const safeDateSuffix = startDate?.replace(/-/g, '') || 'all';
        XLSX.writeFile(workbook, `Publisher_Tagging_${safeDateSuffix}.xlsx`);
    }
    catch (error) {
        console.error('Export failed:', error);
        toast.info('Failed to export. Please try again.');
    }
}
export default exportPublisherTagging;
